export interface ApiMeta {
  api_version?: string;
  request_id?: string;
  tenant_id?: string | null;
  timestamp?: string;
  next_cursor?: string | null;
  nextCursor?: string | null;
  cursor?: string | null;
  nextToken?: string | null;
}

export interface ApiPagination {
  next_cursor?: string | null;
  nextCursor?: string | null;
  cursor?: string | null;
  nextToken?: string | null;
}

export interface ApiResponse<T> {
  code: string;
  data: T;
  message: string;
  meta?: ApiMeta;
  pagination?: ApiPagination | null;
  status: number;
  success: boolean;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function extractCollectionData<T>(response: Partial<ApiResponse<unknown>> | null | undefined): T[] {
  const data = response?.data;
  if (Array.isArray(data)) {
    return data as T[];
  }

  if (isRecord(data)) {
    const items = data['items'];
    if (Array.isArray(items)) {
      return items as T[];
    }

    const item = data['item'];
    if (Array.isArray(item)) {
      return item as T[];
    }
  }

  return [];
}

export function extractItemData<T>(response: Partial<ApiResponse<unknown>> | null | undefined): T {
  const data = response?.data;
  if (isRecord(data) && 'item' in data) {
    return data['item'] as T;
  }
  return data as T;
}

export function extractNextCursor(response: Partial<ApiResponse<unknown>> | null | undefined): string | null {
  const pagination = isRecord(response?.pagination) ? response?.pagination : null;
  const meta = isRecord(response?.meta) ? response?.meta : null;
  const data = isRecord(response?.data) ? response?.data : null;

  const candidates = [
    pagination?.['next_cursor'],
    pagination?.['nextCursor'],
    pagination?.['cursor'],
    pagination?.['nextToken'],
    meta?.['next_cursor'],
    meta?.['nextCursor'],
    meta?.['cursor'],
    meta?.['nextToken'],
    data?.['next_cursor'],
    data?.['nextCursor'],
    data?.['cursor'],
    data?.['nextToken']
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return null;
}

export interface AuthUserPayload {
  email: string;
  name: string | null;
  picture: string | null;
  tenant_id: string | null;
  user_id: string;
}

export interface LoginSuccessPayload {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
  user: AuthUserPayload;
}

export interface RefreshTokenPayload {
  access_token: string;
  expires_in: number;
  id_token: string;
  // Cognito no rota el refresh token: en /auth/token/refresh viene null y se
  // debe conservar el refresh token existente.
  refresh_token: string | null;
  token_type: string;
}

export interface MePayload {
  attributes: {
    email?: string;
    email_verified?: string;
    sub?: string;
    [key: string]: string | undefined;
  };
  mfa_methods: string[] | null;
  mfa_options: string[] | null;
  tenant_id: string | null;
  username: string;
  is_root?: boolean;
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  tokenType: string;
  expiresIn: number;
  user: {
    email: string;
    name: string | null;
    picture: string | null;
    tenantId: string | null;
    userId: string;
    isRoot?: boolean;
  };
}
